// src/routes/AppRoutes.jsx
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";

// RDS UI pages
import RdsRequirementStudio from "../rds_ui/pages/RequirementStudio";
import RdsDesignStudio from "../rds_ui/pages/DesignStudio";
import RdsDesignProjectDetail from "../rds_ui/pages/DesignProjectDetail";
import RdsSOWDetail from "../rds_ui/pages/SOWDetail";
import RdsGenerationProgress from "../rds_ui/pages/GenerationProgress";
import RdsGenerationResults from "../rds_ui/pages/GenerationResults";
import RdsPromptEditor from "../rds_ui/pages/PromptEditor";

import Dashboard from "../pages/Dashboard";
import UsersPage from "../pages/UsersPage";
import RolesPage from "../pages/RolesPage";
import PermissionsPage from "../pages/PermissionsPage";

import AcceleratorsPage from "../pages/AcceleratorsPage.jsx";

import RagCollectionDetailPage from "../pages/RagCollectionDetailPage.jsx";
import RagCollectionsPage from "../pages/RagCollectionsPage.jsx";
import RagSearchPage from "../pages/RagSearchPage.jsx";
import RagAnalyticsPage from "../pages/RagAnalyticsPage.jsx";

import LoginPage from "../pages/LoginPage";
import SsoCallback from "../pages/SsoCallback";
import Unauthorized from "../pages/Unauthorized";
import NotFound from "../pages/NotFound";

import ProtectedOutlet from "../components/ProtectedOutlet";
import ProtectedRoute from "../components/ProtectedRoute";
import RagJobsPage from "../pages/RagJobsPage.jsx";
import RagClientScriptsPage from "../pages/RagClientScriptsPage.jsx";
import DataSourcesPage from "../GDB/pages/DataSourcesPage.jsx";
import DatasetsPage from "../GDB/pages/DatasetBuilderPage.jsx";
import DashboardEditPage from "../GDB/pages/DashboardEditPage.jsx";
import MyDashboardsPage from "../GDB/pages/MyDashboardsPage";
import CSVDatasetsPage from "../GDB/pages/CSVDatasetsPage.jsx";
import CSVDatasetUpload from "../GDB/components/CSVDatasetUpload.jsx";

import RulesConfigPage from "../pages/RulesConfigPage.jsx";
import ReportDetailPage from "../pages/ReportDetailPage.jsx";
import ReportsPage from "../pages/ReportsListPage.jsx";
import SowTestAnalysisPage from "../pages/SowTestAnalysisPage.jsx";
import SowDraftPage from "../pages/SowDraftPage.jsx";
import SowTemplatePage from "../pages/SowTemplatePage.jsx";
// ✅ NEW: SOW Analysis Viewer Page
import SowAnalysisViewerPage from "../pages/Sowanalysisviewerpage.jsx";
// ✅ IT module imports

import ITNewRequest from "../IT/pages/ITNewRequest";
import ITMyRequests from "../IT/pages/ITMyRequests";
import ITApprovals from "../IT/pages/ITApprovals";
import ITRequestDetail from "../IT/pages/ITRequestDetail";
import ITChatbot from "../IT/pages/ITChatbot";

import XenonAiPage from "../xenonai/pages/XenonAiPage.jsx";

// ── Orion GoLive module ───────────────────────────────────────────────────
import GoLiveLayout from "../golive/GoLiveLayout.jsx";
import GoLiveHomePage from "../golive/pages/GoLiveHomePage.jsx";
import RunListPage from "../golive/pages/RunListPage.jsx";
import NewDeploymentPage from "../golive/pages/NewDeploymentPage.jsx";
import RunDetailPage from "../golive/pages/RunDetailPage.jsx";

// ✅ Codebase module imports (NEW)
import RepoListPage from "../codebase/pages/RepoListPage.jsx";
import RepoAddPage from "../codebase/pages/RepoAddPage.jsx";
import RepoEditPage from "../codebase/pages/RepoEditPage.jsx";
import RepoOverviewPage from "../codebase/pages/RepoOverviewPage.jsx";
import ConnectionsListPage from "../codebase/pages/ConnectionsListPage.jsx";
import ConnectionAddPage from "../codebase/pages/ConnectionAddPage.jsx";
import ConnectionEditPage from "../codebase/pages/ConnectionEditPage.jsx";
import ScoringRulesPage from "../codebase/pages/ScoringRulesPage.jsx";

import MCPChat from "../pages/MCPChat.jsx";

// Requirements & Design Studio
import CompliancePage from "../compliance/CompliancePage.jsx";

// Wraps RDS pages with font-size=12px (matching app baseline) and a scoped CSS reset
// that replicates Tailwind preflight for RDS elements without affecting the rest of the app.
function RdsFontReset() {
  useEffect(() => {
    const prev = document.documentElement.style.fontSize;
    document.documentElement.style.fontSize = "12px";
    return () => {
      document.documentElement.style.fontSize = prev;
    };
  }, []);
  return (
    <div className="rds-scope">
      <Outlet />
    </div>
  );
}

import AdaiPage from "../adai/pages/AdaiPage.jsx";
import Faq from "../pages/FAQ.jsx";

function DashboardHome() {
  return (
    <div style={{ maxWidth: 900 }}>
      <h2
        style={{
          fontSize: 16,
          fontWeight: 700,
          margin: "0 0 8px 0",
          color: "#111827",
        }}
      >
        Welcome to Jade Orion!
      </h2>

      <p
        style={{
          fontSize: 12.5,
          lineHeight: 1.6,
          margin: 0,
          color: "#374151",
        }}
      >
        Jade Orion is an enterprise-grade AI platform that unifies entire
        software delivery lifecycle into one intelligent, audit-ready flow. Jade
        Orion is an enterprise-grade AI platform that unifies entire software
        delivery lifecycle into one intelligent, audit-ready flow.
        <br />
        It takes you from RFP response → SOW Analysis/Generation → Requirements
        & Design → Code analysis → Test generation → Deployment & Post-release
        insights, with AI guiding each step and generating artefacts that
        support ISO and CMMI-aligned processes.
        <br />
        Jade Orion acts as a “delivery brain” that sits above tools and
        technologies, orchestrating work across presales, architecture,
        development, QA, DevOps, and compliance.
      </p>

      <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
        Use the left menu to navigate.
      </div>
    </div>
  );
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso/callback" element={<SsoCallback />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Protected shell */}
        <Route element={<ProtectedOutlet />}>
          <Route path="/" element={<Dashboard />}>
            {/* Landing inside dashboard */}
            <Route index element={<DashboardHome />} />

            {/* Admin */}
            <Route
              path="users"
              element={
                <ProtectedRoute requiredPermissions={["Users"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="roles"
              element={
                <ProtectedRoute requiredPermissions={["Roles"]}>
                  <RolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="permissions"
              element={
                <ProtectedRoute requiredPermissions={["Permissions"]}>
                  <PermissionsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="accelerators"
              element={
                <ProtectedRoute requiredPermissions={["Accelerators"]}>
                  <AcceleratorsPage />
                </ProtectedRoute>
              }
            />
            {/* ---------- RAG / KB MODULE ---------- */}
            {/* Primary paths (recommended) */}
            <Route
              path="rag/kb"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagCollectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag/kb/:id"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagCollectionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag/search"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag/analytics"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagAnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Legacy aliases (keep for now so nothing breaks) */}
            <Route
              path="rag-collections"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagCollectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag-collections/:id"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagCollectionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag-search"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagSearchPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="rag-analytics"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagAnalyticsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="rag-jobs"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rag/kb/:id/scripts"
              element={
                <ProtectedRoute requiredPermissions={["RAG"]}>
                  <RagClientScriptsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasources"
              element={
                <ProtectedRoute requiredPermissions={["Dashboard"]}>
                  <DataSourcesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasets"
              element={
                <ProtectedRoute requiredPermissions={["Dashboard"]}>
                  <DatasetsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/csv-datasets"
              element={
                <ProtectedRoute requiredPermissions={["Dashboard"]}>
                  <CSVDatasetsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/csv-upload"
              element={
                <ProtectedRoute requiredPermissions={["Dashboard"]}>
                  <CSVDatasetUpload />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboards"
              element={
                <ProtectedRoute requiredPermissions={["Dashboard"]}>
                  <DashboardEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mydashboards"
              element={
                <ProtectedRoute requiredPermissions={["Dashboard"]}>
                  <MyDashboardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rules-config"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <RulesConfigPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <ReportDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sow-history-analysis"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <SowTestAnalysisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sowdraft"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <SowDraftPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sowtemplate"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <SowTemplatePage />
                </ProtectedRoute>
              }
            />
            {/* ✅ NEW: SOW Analysis Viewer (SFDC + UI) */}
            <Route
              path="/sow-analysis-viewer"
              element={
                <ProtectedRoute requiredPermissions={["SOW"]}>
                  <SowAnalysisViewerPage />
                </ProtectedRoute>
              }
            />

            {/* IDP — Audit & Compliance */}
            <Route path="/compliance" element={<CompliancePage />} />

            <Route
              path="development/adai"
              element={
                <ProtectedRoute requiredPermissions={["ADAI"]}>
                  <AdaiPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/mcp"
              element={
                <ProtectedRoute requiredPermissions={["MCP"]}>
                  <MCPChat />
                </ProtectedRoute>
              }
            />

            <Route
              path="itnew-request"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_CREATE"]}>
                  <ITNewRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="myitrequests"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_CREATE"]}>
                  <ITMyRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="itapprovals"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_APPROVE"]}>
                  <ITApprovals />
                </ProtectedRoute>
              }
            />

            <Route
              path="itreq/:id"
              element={
                <ProtectedRoute
                  requiredPermissions={[
                    "IT_REQUEST_CREATE",
                    "IT_REQUEST_APPROVE",
                    "IT_REQUEST_ADMIN",
                  ]}
                >
                  <ITRequestDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="itchatbot"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_CREATE"]}>
                  <ITChatbot />
                </ProtectedRoute>
              }
            />

            {/* ---------------- CODEBASE ANALYSIS (NEW) ---------------- */}
            <Route
              path="codebase"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <RepoListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="codebase/repos/new"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <RepoAddPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="codebase/repos/:repoId/edit"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <RepoEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="codebase/repos/:repoId"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <RepoOverviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="codebase/connections"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <ConnectionsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="codebase/connections/new"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <ConnectionAddPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="codebase/connections/:id/edit"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <ConnectionEditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="scoringrules"
              element={
                <ProtectedRoute requiredPermissions={["CODEBASE"]}>
                  <ScoringRulesPage />
                </ProtectedRoute>
              }
            />

            {/* ── AI Xenon ─────────────────────────────────────────── */}
            <Route
              path="testing/xenon"
              element={
                <ProtectedRoute requiredPermissions={["XenonAI"]}>
                  <XenonAiPage />
                </ProtectedRoute>
              }
            />

            {/* ── Orion GoLive ─────────────────────────────────────────── */}
            <Route path="golive" element={<GoLiveLayout />}>
              <Route index element={<GoLiveHomePage />} />
              <Route path="new" element={<NewDeploymentPage />} />
              <Route path="history" element={<RunListPage />} />
              <Route path=":runId/*" element={<RunDetailPage />} />
            </Route>

            {/* ── Requirements & Design Studio ─────────────────────────── */}
            <Route element={<RdsFontReset />}>
              <Route
                path="/requirement-studio"
                element={<RdsRequirementStudio />}
              />
              <Route path="/design-studio" element={<RdsDesignStudio />} />
              <Route
                path="/design-studio/projects/:projectId"
                element={<RdsDesignProjectDetail />}
              />
              <Route
                path="/projects/:projectId/sow/:sowId"
                element={<RdsSOWDetail />}
              />
              <Route
                path="/projects/:projectId/generations/:runId"
                element={<RdsGenerationProgress />}
              />
              <Route
                path="/projects/:projectId/generations/:runId/results"
                element={<RdsGenerationResults />}
              />
              <Route
                path="/projects/:projectId/prompts"
                element={<RdsPromptEditor />}
              />
              {/* FAQs */}
              <Route path="faq" element={<Faq/>}/> 
            </Route>
          </Route>

          {/* Redirect old path to new rds_ui path */}
          <Route
            path="/requirements-studio"
            element={<Navigate to="/requirement-studio" replace />}
          />
          <Route
            path="/requirements-studio/*"
            element={<Navigate to="/requirement-studio" replace />}
          />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>   
    </BrowserRouter>
  );
}
